import React, { useState } from 'react';
import { AppState } from '../../store/state';
import { selectConference, selectTrackRoles } from '../../store/selectors';
import { AppDispatch } from '../../store/store';
import Loading from '../../components/Loading/Loading';
import { connect } from 'react-redux';
import styled, { ThemeProvider } from 'styled-components';
import useTheme from '@material-ui/core/styles/useTheme';
import { Button, Select } from '../../components/ui';
import conferenceSlice from '../../store/conference/slice';
import { Announcement, Role } from '../../store/conference/types';
import { Switch } from '@progress/kendo-react-inputs';
import HtmlTextEditor from '../../components/ui/inputs/HtmlTextEditor/HtmlTextEditor';
import { useTranslation } from 'react-i18next';
import { getRoleName } from '../../helpers/translations';
import { HowItWorksButton } from '../../components/ui/inputs/HowItWorksButton/HowItWorksButton';
import SimpleDialog from '../../components/dialogs/SimpleDialog/SimpleDialog';
import DocumentationSidePanel from '../../components/ui/utils/DocumentationSidePanel/DocumentationSidePanel';

interface Props {
  loading: boolean;
  roleById: { [key: string]: Role };
  announcementById: { [key: string]: Announcement };
  postAnnouncementAction: (data: Announcement) => void;
}

const SettingsAnnouncementsPage: React.FC<Props> = ({
  loading,
  postAnnouncementAction,
  roleById,
  announcementById,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const selectOptions = Object.values(roleById)
    .filter((role) => role.type !== 'chair')
    .map((role) => ({ id: role.id.toString(), label: getRoleName(role, t) }));

  const emptyAnnouncement = (roleId: number) => {
    return { role_id: roleId, html: '<p></p>', is_published: false };
  };

  const initAnnouncement = (roleId: number): Announcement => announcementById[roleId] ?? emptyAnnouncement(roleId);

  const [roleId, setRoleId] = useState<number>(() => parseInt(selectOptions[0].id));
  const [announcement, setAnnouncement] = useState<Announcement>(() => initAnnouncement(roleId));
  const [dialog, setDialog] = React.useState<JSX.Element | undefined>(undefined);

  const onRoleChange = (roleId: number) => {
    setRoleId(roleId);
    setAnnouncement(initAnnouncement(roleId));
  };

  const isEmpty = (a: Announcement) => {
    return a.html == emptyAnnouncement(roleId).html;
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <ThemeProvider theme={theme}>
      <div className="w-full flex" style={{ flexGrow: 1 }}>
        <div className="w-full flex flex-col">
          <StyledWrapper className="max-w-5xl">
            <StyledSelectWrapper>
              <div className="flex flex-row">
                <Select
                  label={t('Role')}
                  value={roleId.toString()}
                  onChange={(value) => onRoleChange(parseInt(value))}
                  options={selectOptions}
                />

                <HowItWorksButton src="https://embed.app.guidde.com/playbooks/qNzfHLEu4RxD8BoUcbmURE" />
              </div>

              <div className="flex flex-row items-center justify-end">
                <p className="text-sm mr-2">{t('Published')}</p>
                <Switch
                  checked={announcement.is_published}
                  onChange={(event) => setAnnouncement({ ...announcement, is_published: event.target.value })}
                  onLabel=""
                  offLabel=""
                  disabled={isEmpty(announcement)}
                />
              </div>
            </StyledSelectWrapper>

            <p className="mb-4">
              {t('Put any message or text here that you want the authors or reviewers to see on their homepage.')}
            </p>

            <HtmlTextEditor
              value={announcement.html}
              editable={true}
              onChange={(html) => {
                const newAnnouncement = {
                  ...announcement,
                  html,
                };
                setAnnouncement({
                  ...newAnnouncement,
                  is_published: !isEmpty(newAnnouncement) ? announcement.is_published : false,
                });
              }}
              className="w-full h-40"
            />
          </StyledWrapper>
          {/* FOOTER BUTTONS */}
          <StyledButtonsWrapper className="max-w-5xl">
            <Button
              className="cursor-pointer"
              onClick={() =>
                postAnnouncementAction({ ...announcement, html: isEmpty(announcement) ? '' : announcement.html })
              }
              disabled={JSON.stringify(announcement) == JSON.stringify(initAnnouncement(roleId))}
            >
              <span className=" font-bold">{t('Save')}</span>
            </Button>
          </StyledButtonsWrapper>
        </div>

        <DocumentationSidePanel>
          <h3>Lorem ipsum dolor sit amet consectetur in arcu metus.</h3>
          <p>
            Lorem ipsum dolor sit amet consectetur. Risus mi ultrices dolor id tellus mi tellus. Etiam velit mi in hac
            nunc cras venenatis rhoncus. In cras facilisi feugiat sed nibh posuere sollicitudin faucibus sit. Netus amet
            blandit curabitur arcu. Lobortis ridiculus sapien augue quis amet faucibus.{' '}
          </p>
          <p>
            Nibh sapien sit diam aliquam eu adipiscing viverra faucibus at. Placerat congue pellentesque lobortis
            gravida. Ut aliquet sit mattis pharetra pellentesque hendrerit euismod tortor. Natoque ipsum adipiscing
            ultricies volutpat ultrices lectus maecenas malesuada. Pulvinar vel ac et velit. Adipiscing est bibendum
            quis quam.
          </p>
          <p>
            Volutpat ultrices eget eleifend scelerisque donec id id tristique. Et laoreet et enim ut. Volutpat purus
            nunc ipsum nisl bibendum id suspendisse ridiculus cursus.
          </p>
        </DocumentationSidePanel>
      </div>

      {dialog}
    </ThemeProvider>
  );
};

const mapStateToProps = (state: AppState) => ({
  loading: selectConference(state).loading,
  roleById: selectTrackRoles(state),
  announcementById: selectConference(state).announcementById,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  postAnnouncementAction: (data: Announcement) => dispatch(conferenceSlice.actions.POST_ANNOUNCEMENT(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SettingsAnnouncementsPage);

const StyledButtonsWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: sticky;
  width: 100%;
  bottom: 0;
  padding: 1rem 0;
  background-color: rgb(237, 243, 255);
  border-top: 1px dashed ${(props) => props.theme.palette.secondary.dark};
`;

const StyledWrapper = styled.div`
  flex-direction: column;
  flex-grow: 1;
  border-top: 0;

  .k-iframe {
    height: 97% !important;
  }

  .k-editor-toolbar {
    padding: 10px 14px;
  }

  .k-editor-content {
    padding: 10px;
  }

  .k-editor {
    max-width: 88em;
    max-height: 42em;
    min-width: 65.4rem;
    min-height: 26.5rem;
    height: 35rem;
  }
`;

const StyledSelectWrapper = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 2rem;
  justify-content: space-between;

  .labelSelect {
    align-self: center;
    margin-bottom: 0;
    margin-right: 1rem;
  }
`;
